/**
 * Award Type Code Mappings
 *
 * Comprehensive mappings for USASpending award type codes across all categories:
 * - IDV (Indefinite Delivery Vehicle) types
 * - Contract order types
 * - Grant types
 * - Direct payment types
 * - Loan types
 * - Other assistance types
 *
 * These mappings align with USASpending's official type codes and provide
 * human-readable labels for display throughout the application.
 */
import type { AwardCategory } from '@/types/usaspending'

// ============================================================================
// Individual Type Mappings
// ============================================================================

/**
 * IDV types (vehicle types)
 * Used for indefinite delivery contracts that spawn child orders
 */
export const idvTypeMapping: Record<string, string> = {
  IDV_A: 'GWAC Government Wide Acquisition Contract',
  IDV_B: 'IDC Multi-Agency Contract, Other Indefinite Delivery Contract',
  IDV_B_A: 'IDC Indefinite Delivery Contract / Requirements',
  IDV_B_B: 'IDC Indefinite Delivery Contract / Indefinite Quantity',
  IDV_B_C: 'IDC Indefinite Delivery Contract / Definite Quantity',
  IDV_C: 'FSS Federal Supply Schedule',
  IDV_D: 'BOA Basic Ordering Agreement',
  IDV_E: 'BPA Blanket Purchase Agreement',
}

/**
 * Contract order types
 * Child orders under an IDV or standalone contracts
 */
export const contractTypeMapping: Record<string, string> = {
  A: 'BPA Call',
  B: 'Purchase Order',
  C: 'Delivery Order',
  D: 'Definitive Contract',
}

/**
 * Grant types
 * Federal financial assistance for specific purposes
 */
export const grantTypeMapping: Record<string, string> = {
  '02': 'Block Grant',
  '03': 'Formula Grant',
  '04': 'Project Grant',
  '05': 'Cooperative Agreement',
}

/**
 * Direct payment types
 * Subsidies and direct financial transfers
 */
export const directPaymentTypeMapping: Record<string, string> = {
  '06': 'Direct Payment for Specified Use',
  '10': 'Direct Payment with Unrestricted Use',
}

/**
 * Loan types
 * Federal loans and loan guarantees
 */
export const loanTypeMapping: Record<string, string> = {
  '07': 'Direct Loan',
  '08': 'Guaranteed/Insured Loan',
}

/**
 * Other types
 * Insurance and miscellaneous assistance
 */
export const otherTypeMapping: Record<string, string> = {
  '09': 'Insurance',
  '11': 'Other Financial Assistance',
  '-1': 'Not Specified',
}

/**
 * Action type codes for Financial Assistance awards (FABS)
 * Used in transaction/modification records for grants, loans, etc.
 * Source: gsdm_maps.py TransactionNormalized.action_type_map case_1
 */
export const assistanceActionTypeMapping: Record<string, string> = {
  A: 'New Assistance Award',
  B: 'Continuation',
  C: 'Revision',
  D: 'Funding Adjustment to Completed Project',
}

/**
 * Action type codes for Procurement awards (FPDS)
 * Used in transaction/modification records for contracts and IDVs.
 * Source: gsdm_maps.py change_reasons / TransactionNormalized.action_type_map case_2
 */
export const procurementActionTypeMapping: Record<string, string> = {
  A: 'Additional Work (new agreement, FAR part 6 applies)',
  B: 'Supplemental Agreement for work within scope',
  C: 'Funding Only Action',
  D: 'Change Order',
  E: 'Terminate for Default (complete or partial)',
  F: 'Terminate for Convenience (complete or partial)',
  G: 'Exercise an Option',
  H: 'Definitize Letter Contract',
  J: 'Novation Agreement',
  K: 'Close Out',
  L: 'Definitize Change Order',
  M: 'Other Administrative Action',
  N: 'Legal Contract Cancellation',
  P: 'Representation of Non-Novated Merger/Acquisition',
  R: 'Representation',
  S: 'Change PIID',
  T: 'Transfer Action',
  V: 'Vendor DUNS or Name Change - Non-Novation',
  W: 'Vendor Address Change',
  X: 'Terminate for Cause',
}

/**
 * Short labels for procurement action types - suitable for badge display
 */
export const shortProcurementActionTypeLabels: Record<string, string> = {
  A: 'Additional Work',
  B: 'Supplemental Agmt',
  C: 'Funding Only',
  D: 'Change Order',
  E: 'Terminate Default',
  F: 'Terminate Convenience',
  G: 'Exercise Option',
  H: 'Definitize Letter',
  J: 'Novation',
  K: 'Close Out',
  L: 'Definitize Change',
  M: 'Admin Action',
  N: 'Legal Cancellation',
  P: 'Merger/Acquisition',
  R: 'Representation',
  S: 'Change PIID',
  T: 'Transfer',
  V: 'Vendor Name Change',
  W: 'Vendor Address Change',
  X: 'Terminate Cause',
}

/**
 * Legacy action type mapping (alias for assistance for backwards compatibility)
 * @deprecated Use assistanceActionTypeMapping or procurementActionTypeMapping
 */
export const actionTypeMapping: Record<string, string> =
  assistanceActionTypeMapping

/**
 * Contract pricing type codes
 * Describes how a contract is priced (fixed price vs cost reimbursement, etc.)
 * Source: USASpending API gsdm_maps.py type_of_contract_pricing_map
 */
export const contractPricingTypeMapping: Record<string, string> = {
  A: 'Fixed Price Redetermination',
  B: 'Fixed Price Level of Effort',
  J: 'Firm Fixed Price',
  K: 'Fixed Price with Economic Price Adjustment',
  L: 'Fixed Price Incentive',
  M: 'Fixed Price Award Fee',
  R: 'Cost Plus Award Fee',
  S: 'Cost No Fee',
  T: 'Cost Sharing',
  U: 'Cost Plus Fixed Fee',
  V: 'Cost Plus Incentive Fee',
  Y: 'Time and Materials',
  Z: 'Labor Hours',
  '1': 'Order Dependent',
  '2': 'Combination',
  '3': 'Other',
}

/**
 * Short labels for contract pricing types - suitable for badge display
 */
export const shortPricingTypeLabels: Record<string, string> = {
  A: 'FP Redetermination',
  B: 'FP Level of Effort',
  J: 'Firm Fixed Price',
  K: 'FP w/ EPA',
  L: 'FP Incentive',
  M: 'FP Award Fee',
  R: 'Cost Plus Award Fee',
  S: 'Cost No Fee',
  T: 'Cost Sharing',
  U: 'Cost Plus Fixed Fee',
  V: 'Cost Plus Incentive',
  Y: 'Time & Materials',
  Z: 'Labor Hours',
  '1': 'Order Dependent',
  '2': 'Combination',
  '3': 'Other',
}

// ============================================================================
// Aggregated Mappings
// ============================================================================

/**
 * All award type codes combined across all categories
 */
export const allAwardTypeMapping: Record<string, string> = {
  ...idvTypeMapping,
  ...contractTypeMapping,
  ...grantTypeMapping,
  ...directPaymentTypeMapping,
  ...loanTypeMapping,
  ...otherTypeMapping,
}

/**
 * All assistance type codes (non-procurement)
 * Includes grants, loans, direct payments, insurance, and other
 */
export const assistanceTypeMapping: Record<string, string> = {
  ...grantTypeMapping,
  ...directPaymentTypeMapping,
  ...loanTypeMapping,
  ...otherTypeMapping,
}

/**
 * All procurement type codes
 * Includes IDV vehicles and contract orders
 */
export const procurementTypeMapping: Record<string, string> = {
  ...idvTypeMapping,
  ...contractTypeMapping,
}

/**
 * Non-loan assistance types
 * Grants, direct payments, insurance, and other (excludes loans)
 */
export const nonLoanAssistanceTypeMapping: Record<string, string> = {
  ...grantTypeMapping,
  ...directPaymentTypeMapping,
  ...otherTypeMapping,
}

// ============================================================================
// Type Code to Category Mapping
// ============================================================================

/**
 * Maps type codes to their award category
 * Used to determine category from type code alone
 */
export const allAwardTypesToCategory: Record<string, AwardCategory> = {
  // IDV types
  IDV_A: 'idv',
  IDV_B: 'idv',
  IDV_B_A: 'idv',
  IDV_B_B: 'idv',
  IDV_B_C: 'idv',
  IDV_C: 'idv',
  IDV_D: 'idv',
  IDV_E: 'idv',

  // Contract types
  A: 'contract',
  B: 'contract',
  C: 'contract',
  D: 'contract',

  // Grant types
  '02': 'grant',
  '03': 'grant',
  '04': 'grant',
  '05': 'grant',

  // Direct payment types
  '06': 'direct payment',
  '10': 'direct payment',

  // Loan types
  '07': 'loans',
  '08': 'loans',

  // Other types
  '09': 'insurance',
  '11': 'other',
  '-1': 'other',
}

// ============================================================================
// Subaward Types
// ============================================================================

/**
 * Valid subaward types in USASpending
 */
export const allSubawardTypes = ['grant', 'procurement'] as const

export type SubawardType = (typeof allSubawardTypes)[number]

/**
 * Subaward field mapping
 * Maps subaward types to their corresponding field names in the API
 */
export const subawardFieldMapping: Record<
  SubawardType,
  {
    typeField: string
    typeLabel: string
  }
> = {
  grant: {
    typeField: 'subgrant',
    typeLabel: 'Subgrant',
  },
  procurement: {
    typeField: 'subcontract',
    typeLabel: 'Subcontract',
  },
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract the short name from an IDV type description
 *
 * @example
 * getVehicleShortName('IDV_C') // 'FSS'
 * getVehicleShortName('IDV_E') // 'BPA'
 * getVehicleShortName('IDV_A') // 'GWAC'
 *
 * @param idvType - The IDV type code (e.g., 'IDV_C')
 * @returns The short name abbreviation (e.g., 'FSS')
 */
export function getVehicleShortName(idvType: string): string {
  const description = idvTypeMapping[idvType]
  if (!description) return idvType

  // Extract the short name (first word before the first space or full description)
  // Examples:
  // 'FSS Federal Supply Schedule' -> 'FSS'
  // 'GWAC Government Wide Acquisition Contract' -> 'GWAC'
  // 'IDC Multi-Agency Contract, Other Indefinite Delivery Contract' -> 'IDC'
  const match = description.match(/^([A-Z]+)/)
  return match ? match[1] : idvType
}

/**
 * Get the award category from a type code
 *
 * @example
 * getCategoryFromTypeCode('IDV_C') // 'idv'
 * getCategoryFromTypeCode('02') // 'grant'
 * getCategoryFromTypeCode('07') // 'loans'
 * getCategoryFromTypeCode('unknown') // 'other'
 *
 * @param typeCode - The award type code
 * @returns The award category
 */
export function getCategoryFromTypeCode(typeCode: string): AwardCategory {
  return allAwardTypesToCategory[typeCode] ?? 'other'
}

/**
 * Get the human-readable description for a type code
 *
 * @example
 * getTypeDescription('IDV_C') // 'FSS Federal Supply Schedule'
 * getTypeDescription('02') // 'Block Grant'
 * getTypeDescription('unknown') // 'Unknown Type'
 *
 * @param typeCode - The award type code
 * @returns The human-readable type description
 */
export function getTypeDescription(typeCode: string): string {
  return allAwardTypeMapping[typeCode] ?? 'Unknown Type'
}

/**
 * Check if a type code represents a procurement award (contract or IDV)
 *
 * @param typeCode - The award type code
 * @returns True if the type is a procurement award
 */
export function isProcurementType(typeCode: string): boolean {
  return typeCode in procurementTypeMapping
}

/**
 * Check if a type code represents an assistance award
 *
 * @param typeCode - The award type code
 * @returns True if the type is an assistance award
 */
export function isAssistanceType(typeCode: string): boolean {
  return typeCode in assistanceTypeMapping
}

/**
 * Check if a type code represents an IDV (vehicle)
 *
 * @param typeCode - The award type code
 * @returns True if the type is an IDV
 */
export function isIdvType(typeCode: string): boolean {
  return typeCode in idvTypeMapping
}

/**
 * Check if a type code represents a loan
 *
 * @param typeCode - The award type code
 * @returns True if the type is a loan
 */
export function isLoanType(typeCode: string): boolean {
  return typeCode in loanTypeMapping
}

/**
 * Subaward type display labels
 * Maps raw subaward type values to display labels
 */
export const subawardTypeLabels: Record<string, string> = {
  subcontract: 'Subcontract',
  'sub-contract': 'Subcontract',
  subgrant: 'Subgrant',
  'sub-grant': 'Subgrant',
}

/**
 * Format a subaward type to a human-readable label
 *
 * @example
 * formatSubawardType('subcontract') // 'Subcontract'
 * formatSubawardType('sub-grant') // 'Subgrant'
 * formatSubawardType(undefined) // null
 *
 * @param subawardType - The raw subaward type value
 * @returns Human-readable label or null if not provided
 */
export function formatSubawardType(
  subawardType: string | undefined | null,
): string | null {
  if (!subawardType) return null
  const normalized = subawardType.toLowerCase().replace(/-/g, '')
  return subawardTypeLabels[normalized] ?? subawardType
}

/**
 * Short labels for award types - suitable for badge display
 * Maps type codes to abbreviated labels
 */
export const shortAwardTypeLabels: Record<string, string> = {
  // IDV types - use abbreviation
  IDV_A: 'GWAC',
  IDV_B: 'IDC',
  IDV_B_A: 'IDC/Requirements',
  IDV_B_B: 'IDC/Indefinite Qty',
  IDV_B_C: 'IDC/Definite Qty',
  IDV_C: 'FSS',
  IDV_D: 'BOA',
  IDV_E: 'BPA',
  // Contract types - already short
  A: 'BPA Call',
  B: 'Purchase Order',
  C: 'Delivery Order',
  D: 'Definitive Contract',
  // Grant types
  '02': 'Block Grant',
  '03': 'Formula Grant',
  '04': 'Project Grant',
  '05': 'Cooperative Agmt',
  // Direct payment types
  '06': 'Direct Payment',
  '10': 'Unrestricted Payment',
  // Loan types
  '07': 'Direct Loan',
  '08': 'Guaranteed Loan',
  // Other types
  '09': 'Insurance',
  '11': 'Other Assistance',
  '-1': 'Not Specified',
}

/**
 * Format an action type code to a human-readable label
 *
 * Action types have different meanings for procurement vs assistance awards:
 * - Procurement (contracts/IDVs): Uses FPDS change_reasons (A-X codes)
 * - Assistance (grants/loans): Uses FABS codes (A-D codes)
 *
 * @example
 * formatActionType('G', true) // 'Exercise an Option' (procurement)
 * formatActionType('A', true) // 'Additional Work (new agreement, FAR part 6 applies)'
 * formatActionType('A', false) // 'New Assistance Award' (assistance)
 * formatActionType('M', true) // 'Other Administrative Action'
 * formatActionType('INITIAL AWARD') // 'INITIAL AWARD' (pass-through)
 * formatActionType(undefined) // null
 *
 * @param actionType - The raw action type value (code or description)
 * @param isProcurement - Whether this is a procurement award (contract/IDV). Defaults to true.
 * @returns Human-readable label or the original value if not a code
 */
export function formatActionType(
  actionType: string | undefined | null,
  isProcurement = true,
): string | null {
  if (!actionType) return null

  const upperCode = actionType.toUpperCase()

  // Use the appropriate mapping based on award type
  const mapping = isProcurement
    ? procurementActionTypeMapping
    : assistanceActionTypeMapping

  const actionMapped = mapping[upperCode]
  if (actionMapped) return actionMapped

  // Also check award type codes (API sometimes returns these in action_type field)
  const awardMapped = allAwardTypeMapping[actionType]
  if (awardMapped) return awardMapped

  // Otherwise return the original value (it might already be a description)
  return actionType
}

/**
 * Get both short and full labels for an action type code
 * Useful for displaying a short badge with a full tooltip
 *
 * @param actionType - The raw action type code
 * @param isProcurement - Whether this is a procurement award (contract/IDV). Defaults to true.
 * @returns Object with short and full labels
 */
export function getActionTypeLabels(
  actionType: string | undefined | null,
  isProcurement = true,
): { short: string | null; full: string | null } {
  if (!actionType) return { short: null, full: null }

  const upperCode = actionType.toUpperCase()

  if (isProcurement) {
    const full = procurementActionTypeMapping[upperCode]
    const short = shortProcurementActionTypeLabels[upperCode]
    if (full) {
      return { short: short ?? actionType, full }
    }
  } else {
    const full = assistanceActionTypeMapping[upperCode]
    if (full) {
      return { short: actionType, full }
    }
  }

  // Fallback to award type mapping or pass-through
  const awardMapped = allAwardTypeMapping[actionType]
  if (awardMapped) {
    return { short: actionType, full: awardMapped }
  }

  return { short: actionType, full: actionType }
}

/**
 * Get a short label suitable for badge display
 *
 * @example
 * getShortTypeLabel('IDV_B_B') // 'IDC/Indefinite Qty'
 * getShortTypeLabel('A') // 'BPA Call'
 * getShortTypeLabel('02') // 'Block Grant'
 *
 * @param typeCode - The raw type code
 * @returns Short label for badge display, or the code if no mapping exists
 */
export function getShortTypeLabel(
  typeCode: string | undefined | null,
): string | null {
  if (!typeCode) return null
  return shortAwardTypeLabels[typeCode] ?? typeCode
}

/**
 * Get both short and full labels for a type code
 * Useful for displaying a short badge with a full tooltip
 *
 * @param typeCode - The raw type code
 * @returns Object with short and full labels
 */
export function getTypeLabels(typeCode: string | undefined | null): {
  short: string | null
  full: string | null
} {
  if (!typeCode) return { short: null, full: null }
  return {
    short: shortAwardTypeLabels[typeCode] ?? typeCode,
    full: allAwardTypeMapping[typeCode] ?? typeCode,
  }
}

// ============================================================================
// Badge Configuration for Award Types
// ============================================================================

/**
 * Award type category for badge color grouping.
 * Categories group related award types for consistent visual treatment.
 */
export type AwardTypeBadgeCategory =
  | 'contract'
  | 'idv'
  | 'grant'
  | 'loan'
  | 'direct-payment'
  | 'other'

/**
 * Badge configuration for award type display.
 * Includes short label, Tailwind classes, and category.
 */
export interface AwardTypeBadgeConfig {
  /** Short label for badge display (e.g., "CONTRACT", "IDV", "GRANT") */
  label: string
  /** Tailwind classes for badge styling (background, text color) */
  className: string
  /** Category for grouping/filtering */
  category: AwardTypeBadgeCategory
}

/**
 * Badge color scheme mapping by category.
 * Uses subtle, professional colors with good contrast.
 */
const BADGE_COLORS: Record<AwardTypeBadgeCategory, string> = {
  contract:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  idv: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  grant: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  loan: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'direct-payment':
    'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  other: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
}

/**
 * Short badge labels for award types.
 * More concise than full descriptions for compact display.
 */
const BADGE_LABELS: Record<string, string> = {
  // IDV types
  IDV_A: 'GWAC',
  IDV_B: 'IDC',
  IDV_B_A: 'IDC',
  IDV_B_B: 'IDC',
  IDV_B_C: 'IDC',
  IDV_C: 'FSS',
  IDV_D: 'BOA',
  IDV_E: 'BPA',
  // Contract types
  A: 'BPA CALL',
  B: 'PO',
  C: 'DO',
  D: 'CONTRACT',
  // Grant types
  '02': 'GRANT',
  '03': 'GRANT',
  '04': 'GRANT',
  '05': 'COOP AGMT',
  // Direct payment types
  '06': 'DIRECT PMT',
  '10': 'DIRECT PMT',
  // Loan types
  '07': 'LOAN',
  '08': 'LOAN',
  // Other types
  '09': 'INSURANCE',
  '11': 'OTHER',
  '-1': 'UNKNOWN',
}

/**
 * Get the badge category for an award type code.
 */
function getBadgeCategory(typeCode: string): AwardTypeBadgeCategory {
  // IDV types
  if (typeCode.startsWith('IDV_')) return 'idv'

  // Contract types (A, B, C, D)
  if (['A', 'B', 'C', 'D'].includes(typeCode)) return 'contract'

  // Grant types (02, 03, 04, 05)
  if (['02', '03', '04', '05'].includes(typeCode)) return 'grant'

  // Loan types (07, 08)
  if (['07', '08'].includes(typeCode)) return 'loan'

  // Direct payment types (06, 10)
  if (['06', '10'].includes(typeCode)) return 'direct-payment'

  return 'other'
}

/**
 * Get badge configuration for an award type code.
 * Returns label, Tailwind classes, and category for badge rendering.
 *
 * @example
 * getAwardTypeBadgeConfig('D') // { label: 'CONTRACT', className: 'bg-emerald-100...', category: 'contract' }
 * getAwardTypeBadgeConfig('IDV_C') // { label: 'FSS', className: 'bg-purple-100...', category: 'idv' }
 * getAwardTypeBadgeConfig('04') // { label: 'GRANT', className: 'bg-blue-100...', category: 'grant' }
 *
 * @param typeCode - The award type code
 * @returns Badge configuration with label, className, and category
 */
export function getAwardTypeBadgeConfig(
  typeCode: string | null | undefined,
): AwardTypeBadgeConfig {
  if (!typeCode) {
    return {
      label: '-',
      className: BADGE_COLORS.other,
      category: 'other',
    }
  }

  const category = getBadgeCategory(typeCode)
  const label = BADGE_LABELS[typeCode] ?? typeCode.toUpperCase()

  return {
    label,
    className: BADGE_COLORS[category],
    category,
  }
}

/**
 * Format a contract pricing type code to a human-readable label
 *
 * @example
 * formatPricingType('J') // 'Firm Fixed Price'
 * formatPricingType('Y') // 'Time and Materials'
 * formatPricingType('FIRM FIXED PRICE') // 'FIRM FIXED PRICE' (pass-through)
 * formatPricingType(undefined) // null
 *
 * @param pricingType - The raw pricing type value (code or description)
 * @returns Human-readable label or the original value if not a code
 */
export function formatPricingType(
  pricingType: string | undefined | null,
): string | null {
  if (!pricingType) return null
  // Check for code mapping
  const mapped = contractPricingTypeMapping[pricingType.toUpperCase()]
  if (mapped) return mapped
  // Also check numeric codes as strings
  const numericMapped = contractPricingTypeMapping[pricingType]
  if (numericMapped) return numericMapped
  // Otherwise return the original value (it might already be a description)
  return pricingType
}

/**
 * Get both short and full labels for a pricing type code
 * Useful for displaying a short badge with a full tooltip
 *
 * @example
 * getPricingTypeLabels('J') // { short: 'Firm Fixed Price', full: 'Firm Fixed Price' }
 * getPricingTypeLabels('K') // { short: 'FP w/ EPA', full: 'Fixed Price with Economic Price Adjustment' }
 *
 * @param pricingType - The raw pricing type code
 * @returns Object with short and full labels
 */
export function getPricingTypeLabels(pricingType: string | undefined | null): {
  short: string | null
  full: string | null
} {
  if (!pricingType) return { short: null, full: null }
  const upperCode = pricingType.toUpperCase()
  const shortLabel =
    shortPricingTypeLabels[upperCode] ?? shortPricingTypeLabels[pricingType]
  const fullLabel =
    contractPricingTypeMapping[upperCode] ??
    contractPricingTypeMapping[pricingType]
  return {
    short: shortLabel ?? pricingType,
    full: fullLabel ?? pricingType,
  }
}
